<template>
  <InsurerPage :insurerSlug="$route.params.token">
    <template v-slot="{ insurer }">
      <v-app id="inspire">
        <v-app-bar color="white" elevation="2" app>
          <v-app-bar-title>
            <InsurerLogo :insurer="insurer" small />
          </v-app-bar-title>

          <v-spacer />

          <v-menu>
            <template #activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on">
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>

            <v-list>
              <v-list-item @click="handleClickRestart">
                <v-list-item-title>
                  <v-icon left>mdi-restart</v-icon>
                  Reiniciar
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-app-bar>

        <v-main class="bg-grey-lighten-2">
          <v-container>
            <v-row>
              <v-col>
                <v-stepper v-model="step" :color="insurer.color">
                  <!-- HEADER -->
                  <v-stepper-header>
                    <!-- 1 -->
                    <v-stepper-step
                      step="1"
                      :complete="step > 1"
                      :editable="canGoToStep(1)"
                      @click="goToStep(1)"
                      :color="insurer.color"
                    >
                      Termos e condições
                    </v-stepper-step>

                    <v-divider />

                    <!-- 2 -->
                    <v-stepper-step
                      step="2"
                      :complete="step > 2"
                      :editable="canGoToStep(2)"
                      @click="goToStep(2)"
                      :color="insurer.color"
                    >
                      Participação
                    </v-stepper-step>

                    <v-divider />

                    <!-- 3 -->
                    <v-stepper-step
                      step="3"
                      :complete="step > 3"
                      :color="insurer.color"
                      :editable="canGoToStep(3)"
                      @click="goToStep(3)"
                    >
                      {{
                        claimFlow === "DNA" ? "Local de Risco" : "Equipamentos"
                      }}
                    </v-stepper-step>

                    <v-divider />
                    <!-- FINAL -->
                    <v-stepper-step :step="finalStep" :color="insurer.color">
                      Conclusão
                    </v-stepper-step>
                  </v-stepper-header>

                  <!-- CONTENT -->
                  <v-stepper-items>
                    <!-- STEP 1 -->
                    <v-stepper-content step="1">
                      <TermsAndConditions
                        @continue="setStep(2)"
                        :insurer="insurer"
                      />
                    </v-stepper-content>

                    <!-- STEP 2 -->
                    <v-stepper-content step="2">
                      <Participation
                        @continue="handleParticipationContinue"
                        :insurer="insurer"
                      />
                    </v-stepper-content>

                    <!-- STEP 3 -->
                    <v-stepper-content step="3">
                      <RiskLocationDNA
                        v-if="claimFlow === 'DNA'"
                        :initialVerticalStep="targetVerticalStep"
                        @continue="setStep(finalStep)"
                        :insurer="insurer"
                      />

                      <Equipaments
                        v-if="claimFlow === 'RE'"
                        @continue="setStep(finalStep)"
                        :insurer="insurer"
                      />
                    </v-stepper-content>

                    <!-- STEP FINAL – CONCLUSÃO -->
                    <v-stepper-content :step="finalStep">
                      <ConclusionRE
                        v-if="claimFlow === 'RE'"
                        ref="conclusion"
                        @continue="handleClickConfirm"
                        :loading="loading"
                        :insurer="insurer"
                      />

                      <ConclusionDNA
                        v-if="claimFlow === 'DNA'"
                        ref="conclusion"
                        :loading="loading"
                        @continue="handleClickConfirm"
                        @edit="handleEditFromConclusion"
                        :insurer="insurer"
                      />
                    </v-stepper-content>
                  </v-stepper-items>
                </v-stepper>
              </v-col>
            </v-row>
          </v-container>
        </v-main>
      </v-app>
    </template>
  </InsurerPage>
</template>

<script>
import TermsAndConditions from "@/components/customers/TermsAndConditions.vue";
import Participation from "@/components/customers/Participation.vue";
import Equipaments from "@/components/customers/Equipaments.vue";
import ConclusionDNA from "@/components/customers/ConclusionDNA.vue";
import ConclusionRE from "@/components/customers/ConclusionRE.vue";
import RiskLocationDNA from "@/components/customers/RiskLocationDNA.vue";
import { generateProcess } from "@/services/customer_inbound";
import InsurerPage from "@/components/insurers/InsurerPage";
import InsurerLogo from "@/components/insurers/InsurerLogo.vue";

export default {
  components: {
    TermsAndConditions,
    Participation,
    Equipaments,
    ConclusionDNA,
    ConclusionRE,
    RiskLocationDNA,
    InsurerPage,
    InsurerLogo,
  },

  data() {
    return {
      step: "1",
      claimFlow: null,
      loading: false,
      targetVerticalStep: null,
    };
  },

  computed: {
    finalStep() {
      return "4";
    },
  },

  watch: {
    step(value) {
      if (value === this.finalStep) {
        this.$refs.conclusion?.refresh();
      }
    },
  },

  methods: {
    setStep(step) {
      this.step = step;
      localStorage.setItem("step", step);
    },

    handleParticipationContinue({ flow }) {
      this.claimFlow = flow;
      localStorage.setItem("claim_flow", flow);
      this.setStep("3");
    },

    canGoToStep(target) {
      return Number(target) < Number(this.step);
    },

    goToStep(target) {
      if (!this.canGoToStep(target)) return;

      if (target === 2) {
        this.claimFlow = null;
        localStorage.removeItem("claim_flow");
      }

      this.setStep(String(target));
    },

    resetFlow() {
      const keys = [
        "step",
        "claim_flow",
        "date",
        "participation",
        "insured",
        "participants",
        "dna_documents",
        "risk_location_damages",
        "equipaments",
      ];

      keys.forEach((key) => localStorage.removeItem(key));
    },

    handleClickRestart() {
      if (!confirm("Reiniciar interação?")) return;

      this.resetFlow();

      this.$router.replace({ name: "customers-inbound" });
    },

    async handleClickConfirm(payload) {
      this.loading = true;

      let processNumber = null;

      if (this.claimFlow === "RE") {
        const { data } = await generateProcess(payload);
        processNumber = data.lead_number;
      }

      this.loading = false;

      this.resetFlow();

      this.$router.replace({
        name: "customers-success",
        query: processNumber ? { process: processNumber } : {},
      });
    },

    handleEditFromConclusion({ horizontalStep, verticalStep }) {
      this.targetVerticalStep = verticalStep || null;
      this.setStep(horizontalStep);
    },
  },

  created() {
    this.step = localStorage.getItem("step") || "1";
    this.claimFlow = localStorage.getItem("claim_flow");
  },
};
</script>
